import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { AdminRegionSummary } from './types';

interface Props {
  id?: string;
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  /** Si true, añade `— Sin asignar —` como opción vacía. */
  allowEmpty?: boolean;
  className?: string;
}

/**
 * Selector de provincia/región agrupado por Comunidad Autónoma.
 * Las opciones se cargan desde `GET /admin/regions` (catálogo estático
 * mantenido en backend para evitar duplicación entre BE y FE).
 */
export function RegionSelect({ id, value, onChange, disabled, allowEmpty, className }: Props) {
  const regions = useQuery({
    queryKey: ['admin', 'regions'],
    queryFn: () => api<AdminRegionSummary[]>('/admin/holidays/regions'),
    // Catálogo estático — no refetch
    staleTime: Infinity,
  });

  const grouped = useMemo(() => {
    if (!regions.data) return [] as Array<{ ca: string; items: AdminRegionSummary[] }>;
    const byCa = new Map<string, AdminRegionSummary[]>();
    for (const r of regions.data) {
      const arr = byCa.get(r.ca) ?? [];
      arr.push(r);
      byCa.set(r.ca, arr);
    }
    return Array.from(byCa.entries())
      .sort((a, b) => a[0].localeCompare(b[0], 'es'))
      .map(([ca, items]) => ({ ca, items }));
  }, [regions.data]);

  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || regions.isLoading}
      className={
        className ??
        'h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none transition-colors disabled:opacity-50'
      }
    >
      {allowEmpty ? <option value="">— Sin asignar —</option> : null}
      {grouped.map((g) => (
        <optgroup key={g.ca} label={g.ca}>
          {g.items.map((r) => (
            <option key={r.code} value={r.code}>
              {r.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
